import type { ForwardedRef, ReactNode } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import {
  type ImageFallbackType,
  setFallbackImage,
} from "../../utils/imageFallback";

export const Card = forwardRef(
  (
    {
      children,
      className,
      onClick,
    }: {
      children?: ReactNode;
      className?: string;
      onClick?: () => void;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <div
        ref={ref}
        className={twMerge(
          "rounded-lg",
          className,
          onClick && "cursor-pointer",
        )}
        onClick={onClick}
      >
        {children}
      </div>
    );
  },
);

export const CardHeader = ({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={twMerge(
        "min-h-[65px] rounded-t-lg border-b border-solid border-gray-200 pb-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

Card.displayName = "Card";

export const CardContent = ({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <div className={twMerge("pt-4", className)} onClick={onClick}>
      {children}
    </div>
  );
};

export const CardImage = ({
  position = "top",
  imageUrl,
  className,
  fallback,
}: {
  position?: "top" | "left";
  imageUrl: string;
  className?: string;
  fallback?: ImageFallbackType;
}) => {
  const image = setFallbackImage({ img: imageUrl, fallback: fallback });
  return (
    <div
      style={{
        ...(position === "left"
          ? {
              aspectRatio: "1 / 1",
            }
          : {}),
        backgroundImage: `url(${image}?w=800)`,
      }}
      className={twMerge(
        "bg-cover bg-center bg-no-repeat",
        position === "top" && "h-[200px] rounded-t-lg",
        position === "left" &&
          "float-left aspect-square h-full w-full rounded-l-lg",
        className,
      )}
    />
  );
};
